import React, { useState, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import auth_service from "../../../src/services/auth_service";
import api_service from "../../../src/services/api_services";
import toast from "react-hot-toast";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FaPhone, FaGoogle } from "react-icons/fa";
import SkeletonLoading from "../../components/SkeletonLoading";
import { useWishlist } from "../../context/WishlistContext";


const SignIn = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errPhoneNumber, setErrPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [referralCodeOfExistingUser, setReferralCodeOfExistingUser] = useState("");
  const [customerIdOfExistingUser, setCustomerIdOfExistingUser] = useState(0);
  const [walletAmountOfExistingUser, setWalletAmountOfExistingUser] = useState(0);
  const [isValidReferralUrl, setIsValidReferralUrl] = useState(null); // null = loading, true = valid, false = invalid
  const [isCheckingReferral, setIsCheckingReferral] = useState(false);
  const hasValidatedRef = useRef(false);
  const { syncWishlistOnLogin } = useWishlist();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get the redirect path from location state or default to home
  const from = location.state?.from || "/";
  const googleId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  
  // Extract referral parameters from URL (memoized to prevent re-creation)
  const { referralCode, referrerCustomerId, hasReferralParams } = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('referralCode');
    const customerId = urlParams.get('customerId');
    return {
      referralCode: code,
      referrerCustomerId: customerId,
      hasReferralParams: code && customerId
    };
  }, [location.search]);
  
  // Move handleLoginSuccess before useEffect to avoid dependency issues
  const handleLoginSuccess = useCallback(() => {
    // Redirect to the page they were trying to access
    navigate(from, { replace: true });
  }, [navigate, from]);
  
  // Validate referral URL and fetch referrer details
  React.useEffect(() => {
    const validateReferralUrl = async () => {
      // Prevent multiple validations
      if (hasValidatedRef.current) {
        return;
      }
      
      // If no referral params, it's a normal login (valid)
      if (!hasReferralParams) {
        setIsValidReferralUrl(true);
        hasValidatedRef.current = true;
        return;
      }

      // Check if user already has a valid token
      const existingToken = Cookies.get("accessToken");
      if (existingToken) {
        try {
          // Verify if the token is valid by checking token data
          const tokenData = auth_service.getAccessTokenData();
          if (tokenData && tokenData.id) {
            console.log('User already authenticated, redirecting to application...');
            // User is already logged in, ignore referral and redirect
            handleLoginSuccess();
            return;
          }
        } catch (error) {
          // Token is invalid, remove it and continue with referral logic
          console.log('Invalid token found, removing and continuing with referral...');
          Cookies.remove("accessToken");
          Cookies.remove("refreshToken");
        }
      }

      setIsCheckingReferral(true);
      
      try {
        // Validate referral parameters
        if (!referralCode || !referrerCustomerId || isNaN(referrerCustomerId)) {
          setIsValidReferralUrl(false);
          return;
        }

        // Fetch and validate referrer details
        const getExistingUData = await api_service.getRef(+referrerCustomerId);
        
        if (!getExistingUData || !getExistingUData.data || !getExistingUData.data[0]) {
          setIsValidReferralUrl(false);
          return;
        }

        const userData = getExistingUData.data[0];
        
        // Validate that the referral code matches
        if (userData.referral_code !== referralCode) {
          setIsValidReferralUrl(false);
          return;
        }

        // Valid referral URL - set the data
        setReferralCodeOfExistingUser(userData.referral_code);
        setWalletAmountOfExistingUser(userData.wallet);
        setCustomerIdOfExistingUser(userData.id);
        setIsValidReferralUrl(true);
        hasValidatedRef.current = true;
        
      } catch (error) {
        console.error('Error validating referral URL:', error);
        setIsValidReferralUrl(false);
        hasValidatedRef.current = true;
      } finally {
        setIsCheckingReferral(false);
      }
    };

    validateReferralUrl();
  }, []); // Empty dependency array - run only once on mount

  const handlePhoneNumber = (e) => {
    setPhoneNumber(e.target.value);
    setErrPhoneNumber("");
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    // Validation for input fields
    if (!phoneNumber) {
      setErrPhoneNumber("Enter your phone number");
      return;
    }

    try {
      // Use auth_service.Login for the API call
      const data = await auth_service.login(phoneNumber);
      console.log(data)
      // Success: Set tokens in cookies and navigate
      if (data.success === true && data.message === "OTP sent successfully") {
        // Set tokens in browser cookies (Uncomment if needed)
        // Cookies.set("accessToken", data.accessToken, { expires: 7 }); // Expires in 1 day
        // Cookies.set("refreshToken", data.refreshToken, { expires: 7 }); // Expires in 7 days

        // Navigate to VerifyOTP with referral data if available
        toast.success("OTP sent successfully", {
          duration: 3000,
          position: "top-center",
          style: {
            background: "#333",
            color: "#fff",
          },
        });
        navigate("/verifyOtp", { 
          state: { 
            phoneNumber,
            referralCode: referralCode || null,
            referrerCustomerId: referrerCustomerId || null
          } 
        });
      } else if(data.success === false && data.message === "user not found"){
        toast.error("User account not found, please signup", {
          duration: 3000,
          position: "top-center",
          style: {
            background: "#333",
            color: "#fff",
          },
        });
      } else {
        // If the success flag is false, display an error toast
        setError("Invalid login credentials");
        toast.error("Something went wrong", {
          duration: 3000,
          position: "top-center",
          style: {
            background: "#333",
            color: "#fff",
          },
        });
      }
    } catch (err) {
      // If there's an error during the API call, display the error toast
      setError("Failed to sign in. Please try again.");
      toast.error("Failed to sign in. Please try again", {
        duration: 3000,
        position: "top-center",
        style: {
          background: "#333",
          color: "#fff",
        },
      });
    }
  };

  const handleGoogleSignInSuccess = async (credentialResponse) => {
    try {
      // Process the Google credential (e.g., send it to your server for verification)
      const token = credentialResponse.credential;
      
      // Decode the JWT token to get user email
      let userEmail = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userEmail = payload.email;
        console.log("Google user email:", userEmail);
      } catch (error) {
        console.error("Error decoding Google token:", error);
      }
      
      // Check if user exists before login
      let isExistingUser = false;
      if (userEmail) {
        try {
          isExistingUser = await api_service.checkIdentifierExists(userEmail);
          console.log("User exists:", isExistingUser);
        } catch (error) {
          console.error("Error checking user existence:", error);
          // Continue with login process even if check fails
        }
      }
      
      const data = await auth_service.loginWithGoogle(token); // Await the API call
      if (data.success === true) {
        Cookies.set("accessToken", data.accessToken, { expires: 7 });
        Cookies.set("refreshToken", data.refreshToken, { expires: 14 });
        
        const customerId = auth_service.getAccessTokenData()?.id;
        
        // Process referral only if user is new and referral parameters exist
        const shouldProcessReferral = !isExistingUser && referralCode && referrerCustomerId && customerId;
        
        if (shouldProcessReferral) {
          console.log("Processing referral for new user:", { customerId, referrerCustomerId, referralCode });
          
          const user = {
            wallet: 50,
            referred_by: referralCode,
            id: customerId,
          };
          
          let user1 = {
            id: +referrerCustomerId,
          };

          try {
            // if(walletAmountOfExistingUser != null){
            //    user1.wallet = walletAmountOfExistingUser + 50;
            // }else{
            //    user1.wallet = 50;
            // }
           
            // Process referral using editProfile method
            const referralResponse = await api_service.updateReferalForNewUSer(user);
            const referralResponse1 = await api_service.updateReferalForExistingUSer(user1);
           
            toast.success("🎉 Welcome! You and your friend both earned 50 points!", {
              duration: 2000,
              position: "top-center",
              style: {
                background: "#10B981",
                color: "#fff",
              },
            });
          } catch (error) {
            console.error("Error processing referral:", error);
            
            toast.error("Referral processing failed, but your login was successful!", {
              duration: 3000,
              position: "top-center",
            });
          
        } 
        }
        
        // Use handleLoginSuccess here instead of navigate("/")
        handleLoginSuccess();
        
        // Fetch the cart from localStorage
        try {
          const cart = JSON.parse(localStorage.getItem("cart")) || [];
          const updatedCart = cart.map((item) => ({
              ...item,
              customer_id: customerId,
          }));
      
          const response = await api_service.addMultipleItemsToCart(updatedCart);
          if (response.success === true) {
              localStorage.removeItem("cart");
          }
        } catch (error) {
            console.error("Error adding items to cart:", error);
        }
        
        try {
            // Sync wishlist on login using the context
            await syncWishlistOnLogin();
        } catch (error) {
            console.error("Error syncing wishlist on login:", error);
        }
      }
    } catch (error) {
      console.error("Error during Google sign-in:", error);
      toast.error("Google sign-in failed. Please try again.", {
        duration: 3000,
        position: "top-center",
        style: {
          background: "#333",
          color: "#fff",
        },
      });
    }
  };

  const handleGoogleSignInError = () => {
    console.log("Google Sign-In failed");
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await handleSignUp(e);
    setIsSubmitting(false);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  // Invalid URL Component
  const InvalidUrlComponent = () => (
    <div
      className="w-full min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background: `linear-gradient(to right, rgb(31, 80, 154), rgb(0, 0, 102))`,
      }}
    >
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="bg-white shadow-2xl rounded-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div 
          className="p-8 text-white text-center"
          style={{
            background: `linear-gradient(to right, rgb(220, 38, 38), rgb(185, 28, 28))`
          }}
        >
          <div className="w-12 h-1 bg-gradient-to-r from-red-400 to-red-500 mx-auto mb-6 rounded-full"></div>
          
          <motion.h1 
            variants={itemVariants}
            className="text-3xl font-bold mb-2"
          >
            Invalid URL
          </motion.h1>
          <motion.p 
            variants={itemVariants}
            className="text-red-100 opacity-90"
          >
            The referral link is not valid
          </motion.p>
        </div>

        <div className="p-8 text-center">
          <motion.div 
            variants={itemVariants}
            className="mb-8"
          >
            {/* Error Icon */}
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg 
                className="w-8 h-8 text-red-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
                />
              </svg>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Oops! Something went wrong
            </h2>
            <p className="text-gray-600 mb-6">
              The referral link you're trying to use is invalid or has expired. 
              Please check the link or contact the person who shared it with you.
            </p>
            
            <div className="space-y-3">
              <motion.button
                variants={itemVariants}
                onClick={() => navigate("/signin")}
                className="w-full py-3 px-4 rounded-lg text-white font-medium transition-all duration-200 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                style={{
                  background: `linear-gradient(to right, rgb(31, 80, 154), rgb(0, 0, 102))`,
                }}
              >
                Continue to Sign In
              </motion.button>
              
              <motion.button
                variants={itemVariants}
                onClick={() => navigate("/signup")}
                className="w-full py-3 px-4 rounded-lg border-2 border-gray-300 text-gray-700 font-medium transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Create New Account
              </motion.button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );

  // Loading Component
  const LoadingComponent = () => (
    <div
      className="w-full min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background: `linear-gradient(to right, rgb(31, 80, 154), rgb(0, 0, 102))`,
      }}
    >
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="bg-white shadow-2xl rounded-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <p className="text-gray-600">Validating referral link...</p>
        </div>
      </motion.div>
    </div>
  );

  // Show loading while checking referral
  if (isCheckingReferral) {
    return <LoadingComponent />;
  }

  // Show invalid URL if validation failed
  if (isValidReferralUrl === false) {
    return <InvalidUrlComponent />;
  }

  // Show login form if URL is valid or no referral params
  return (
    <GoogleOAuthProvider clientId={googleId}>
      <div
        className="w-full min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
        style={{
          background: `linear-gradient(to right, rgb(31, 80, 154), rgb(0, 0, 102))`,
        }}
      >
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="bg-white shadow-2xl rounded-2xl w-full max-w-md overflow-hidden"
        >
          {/* Modern Header */}
          <div 
            className="p-8 text-white text-center"
            style={{
              background: `linear-gradient(to right, rgb(31, 80, 154), rgb(0, 0, 102))`
            }}
          >
            <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-amber-500 mx-auto mb-6 rounded-full"></div>
            
            <motion.h1 
              variants={itemVariants}
              className="text-3xl font-bold mb-2"
            >
              Welcome Back
            </motion.h1>
            <motion.p 
              variants={itemVariants}
              className="text-blue-100 opacity-90"
            >
              Sign in to your account
            </motion.p>
          </div>

          <div className="p-8">
          {/* Referral message */}
              {hasReferralParams && referralCode === referralCodeOfExistingUser && customerIdOfExistingUser === +referrerCustomerId && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 p-4 mb-6 rounded-xl"
                >
                  <div className="text-center">
                    <p className="text-amber-800 font-semibold">🎉 Referral Bonus!</p>
                    <p className="text-amber-700 text-sm mt-1">
                      You and your friend will both earn <strong>50 points</strong> when you sign in!
                    </p>
                  </div>
                </motion.div>
              )}

            
            <form
              className="space-y-6"
              onSubmit={handleSubmit}
            >
              {/* Error message */}
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 p-4 rounded-lg"
                >
                  <p className="text-red-800 text-sm">{error}</p>
                </motion.div>
              )}

              {/* Phone Number Input */}
              <motion.div variants={itemVariants} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="relative">
                 
                  <input
                    onChange={handlePhoneNumber}
                    value={phoneNumber}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                    type="tel"
                    placeholder="Enter your phone number"
                  />
                </div>
                {errPhoneNumber && (
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-600 text-sm"
                  >
                    {errPhoneNumber}
                  </motion.p>
                )}
              </motion.div>

              {/* Sign In Button */}
              <motion.button
                variants={itemVariants}
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-4 rounded-lg text-white font-medium transition-all duration-200 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50"
                style={{
                  background: `linear-gradient(to right, rgb(31, 80, 154), rgb(0, 0, 102))`,
                }}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  "Sign In"
                )}
              </motion.button>

              {/* Divider */}
              <motion.div variants={itemVariants} className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </motion.div>

              {/* Google Sign In */}
              <motion.div variants={itemVariants} className="flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSignInSuccess}
                  onError={handleGoogleSignInError}
                  useOneTap={false}
                  theme="filled_blue"
                  shape="pill"
                  size="large"
                  text="signin_with"
                  locale="en"
                />
              </motion.div>

              {/* Sign Up Link */}
              <motion.p variants={itemVariants} className="text-sm text-center mt-6 text-gray-600">
                Don't have an account?{" "}
                <Link 
                  to={hasReferralParams && isValidReferralUrl === true 
                    ? `/signup?referralCode=${referralCode}&customerId=${referrerCustomerId}`
                    : "/signup"
                  }
                  className="font-medium text-amber-600 hover:text-amber-500 transition-colors duration-200"
                >
                  Sign up now
                </Link>
              </motion.p>
            </form>
          </div>
        </motion.div>
      </div>
    </GoogleOAuthProvider>
  );
};

// Remove the handleLoginSuccess function from here

export default SignIn;